import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { axiosClient } from '@as-react/common-utils';
import {
  AlternativePaymentData,
  AppliedVoucher,
  CheckoutInitSetState,
  ClickAndCollectStore,
  Cookie,
  DeliveryCountriesResponse,
  DeliveryDate,
  DeliveryInfo,
  OrderInfo,
  PaymentData,
  PaymentInfo,
  PaymentProvider,
  PaymentProviderInformation,
  PaymentRequestConfirmation,
  PaymentRequestInfo,
  PaymentRequestProviderInfo,
  PickupInformation,
  PickupPoint,
  SaferpayAliasStatus,
  SaferpayAltStatus,
} from '@as-react/common-types';
import { CookieUtil } from '@as-react/common-utils';
import { QueryApiOptions } from '../../types/types';
import { createUrl } from '../../utils/createUrl';

export enum CheckoutVersion {
  OLD = 1,
  NEW = 2,
}

export type GetPickupPointsParams = {
  postcode: string;
  provider: string;
  countryCode: string;
  deliveryDate: string;
  limit: number;
};

export type PostPickupPointParams = {
  pickupPoint: PickupPoint;
  provider: string;
  countryCode: string;
};

export type GetStoresForGeoLocationParams = {
  latitude: number;
  longitude: number;
  size: number;
  onlyInStock: boolean;
  fasciaFirst?: boolean;
};

export type PostSubmitOrderParams = {
  provider: PaymentProvider;
  authorizationToken: string;
};

export type PostVoucherSubmitParams = {
  code: string;
  webcode: string;
};

export type GetPaymentRequestParams = {
  paymentRequestId: string;
};

export type PostRequestSaferpayTokenParams = {
  paymentRequestId: string;
  saferpayFieldsToken: string;
};

export type PostPaymentRequestParams = {
  paymentRequestId: string;
  payment: PaymentData;
};

export type PaymentProviderInputRequestParams = {
  provider: PaymentProvider;
  data?: {
    fictiveWebshopId: string;
    market: string;
    paymentRequestId: string;
  };
};

const checkoutEndpoints = {
  initiate: { client: 'api/checkout/initiate' },
  contact: { client: 'api/checkout/contact' },
  dtmCheckoutStep: { client: 'api/dtm/checkoutstep' },
  getDeliveryCountries: { client: 'api/deliverycharges/countries/{mainWebshop}/{shopId}' },
  postDeliveryCountry: { client: 'api/buy/delivery/country/{countryId}' },
  getDeliveryInfo: { client: 'api/buy/delivery' },
  postDeliveryOption: { client: 'api/buy/delivery/option/{deliveryOption}' },
  postDelivery: { client: 'api/buy/delivery' },
  getPickupPoints: { client: 'api/pickup/postcode' },
  postPickupPoint: { client: 'api/buy/pickup/v2' },
  collectInStore: { client: 'api/buy/delivery/clickandcollect' },
  getPaymentInfo: { client: 'api/buy/payment' },
  namedDayDeliveryDates: { client: 'api/nameddaydelivery/deliverydays' },
  postPaymentOptionId: { client: 'api/buy/payment/option/{optionId}' },
  getSaferpayAltStatus: { client: 'api/buy/status/saferpay/alternative' },
  postSaferpayAlt: { client: 'api/buy/input/saferpay/alternative' },
  postSaferpayToken: { client: 'api/buy/input/saferpay/token' },
  postShoplandingPayment: { client: 'api/buy/landing' },
  postOrderPaidByVoucher: { client: 'api/buy/payment/voucher' },
  postPayment: { client: 'api/buy/payment/v2' },
  postPaymentProviderInput: { client: 'api/buy/input/{provider}' },
  postSubmitOrder: { client: 'api/buy/submit/{provider}' },
  postSaferpayAlias: { client: 'api/buy/input/saferpay/alias' },
  postSubmitVoucher: { client: 'api/checkout/voucher' },
};

const paymentRequestEndpoints = {
  paymentRequest: 'api/buy/paymentrequest/payment',
  paymentRequestConfirmation: 'api/checkout/paymentrequest/done',
  paymentSaferpayToken: 'api/buy/paymentrequest/input/saferpay/token', // Bancontact & Credit Card
  postPaymentRequest: 'api/buy/paymentrequest/payment/v2', // Third Party
  providerPaymentRequest: 'api/buy/paymentrequest/input', // Third Party
  initSaferpayAlternative: 'api/buy/paymentrequest/input/saferpay/alternative', // QR Code
  statusSaferpayAlternative: 'api/buy/status/saferpay/alternative', // QR code
};

export const initRequest = async ({
  servicesEndpoint,
  isServer,
  defaultRequestParams,
}: QueryApiOptions): Promise<CheckoutInitSetState> => {
  const url = createUrl(servicesEndpoint, checkoutEndpoints.initiate, isServer);

  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url,
    params: { ...defaultRequestParams, checkoutVersion: CheckoutVersion.NEW },
    withCredentials: true,
    data: {
      adobeUuid: CookieUtil.get(Cookie._UUID),
      visitorId: CookieUtil.getByRegexp(/^AMCV_.+AdobeOrg/i),
      userAgent: navigator.userAgent,
      fredhopperSegment: CookieUtil.get(Cookie.FREDHOPPER_SEGMENT),
      consentSetting: CookieUtil.get(Cookie.CONSENT_SETTING),
    },
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const getDeliveryCountries = async ({
  servicesEndpoint,
  isServer,
  defaultRequestParams,
}: QueryApiOptions): Promise<DeliveryCountriesResponse> => {
  const url = createUrl(servicesEndpoint, checkoutEndpoints.getDeliveryCountries, isServer, {
    mainWebshop: defaultRequestParams.mainWebshop,
    shopId: defaultRequestParams.shopId,
  });
  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    params: defaultRequestParams,
    withCredentials: true,
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const postDeliveryCountry = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  countryId: number
): Promise<void> => {
  const url = createUrl(servicesEndpoint, checkoutEndpoints.postDeliveryCountry, isServer, {
    countryId,
  });
  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url,
    params: defaultRequestParams,
    withCredentials: true,
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const getDeliveryInfo = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  countryId?: number
): Promise<DeliveryInfo> => {
  const url = createUrl(servicesEndpoint, checkoutEndpoints.getDeliveryInfo, isServer);

  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    params: { ...defaultRequestParams, countryId },
    withCredentials: true,
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const postDeliveryOption = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  deliveryOption: string
): Promise<void> => {
  const url = createUrl(servicesEndpoint, checkoutEndpoints.postDeliveryOption, isServer, {
    deliveryOption,
  });

  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url,
    params: defaultRequestParams,
    withCredentials: true,
  };

  await axiosClient.request(axiosConfig);
};

export const postDelivery = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  deliveryInfo: DeliveryInfo
): Promise<void> => {
  const url = createUrl(servicesEndpoint, checkoutEndpoints.postDelivery, isServer);

  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url,
    params: defaultRequestParams,
    withCredentials: true,
    data: deliveryInfo,
  };

  await axiosClient.request(axiosConfig);
};

export const getPickupPoints = async (
  { servicesEndpoint, isServer }: QueryApiOptions,
  { postcode, provider, countryCode, deliveryDate, limit }: GetPickupPointsParams
): Promise<AxiosResponse<Array<PickupPoint>>> => {
  const url = createUrl(servicesEndpoint, checkoutEndpoints.getPickupPoints, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    params: { postcode, provider, countryCode, deliveryDate, limit },
    withCredentials: true,
  };

  return await axiosClient.request(axiosConfig);
};

export const postPickupPoint = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  { pickupPoint, provider, countryCode }: PostPickupPointParams
): Promise<AxiosResponse<PickupInformation>> => {
  const url = createUrl(servicesEndpoint, checkoutEndpoints.postPickupPoint, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url,
    params: { ...defaultRequestParams, provider },
    data: { ...pickupPoint, countryCode },
    withCredentials: true,
  };

  return await axiosClient.request(axiosConfig);
};

export const getStoresForGeoLocation = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  { fasciaFirst = false, ...params }: GetStoresForGeoLocationParams
): Promise<Array<ClickAndCollectStore>> => {
  const url = createUrl(servicesEndpoint, checkoutEndpoints.collectInStore, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    params: { ...defaultRequestParams, ...params, fasciaFirst },
    withCredentials: true,
  };

  const answer = await axiosClient.request(axiosConfig);
  return answer.data;
};

export const postClickAndCollectStoreId = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  storeId: number
): Promise<void> => {
  const url = createUrl(servicesEndpoint, checkoutEndpoints.collectInStore, isServer);
  CookieUtil.set(Cookie.CLICK_AND_COLLECT_STORE, storeId.toString(), { secure: true });

  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url,
    params: { ...defaultRequestParams, storeId },
    withCredentials: true,
  };

  return axiosClient.request(axiosConfig);
};

export const getPaymentInfo = async ({
  servicesEndpoint,
  isServer,
  defaultRequestParams,
}: QueryApiOptions): Promise<PaymentInfo> => {
  const url = createUrl(servicesEndpoint, checkoutEndpoints.getPaymentInfo, isServer);

  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    params: defaultRequestParams,
    withCredentials: true,
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const postPaymentOptionId = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  optionId: number
): Promise<void> => {
  const url = createUrl(servicesEndpoint, checkoutEndpoints.postPaymentOptionId, isServer, {
    optionId,
  });
  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url,
    params: defaultRequestParams,
    withCredentials: true,
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const getNamedDayDeliveryDates = async ({
  servicesEndpoint,
  defaultRequestParams,
}: QueryApiOptions): Promise<Array<DeliveryDate>> => {
  const url = createUrl(servicesEndpoint, checkoutEndpoints.namedDayDeliveryDates);
  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    params: { ...defaultRequestParams },
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const getSaferpayAltStatus = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  requestId: string
): Promise<SaferpayAltStatus> => {
  const url = createUrl(servicesEndpoint, checkoutEndpoints.getSaferpayAltStatus, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    params: { ...defaultRequestParams, requestId, checkoutVersion: CheckoutVersion.NEW },
    withCredentials: true,
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const postSaferpayAlt = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  isMobileDevice: boolean
): Promise<AlternativePaymentData> => {
  const url = createUrl(servicesEndpoint, checkoutEndpoints.postSaferpayAlt, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url,
    params: {
      ...defaultRequestParams,
      checkoutVersion: CheckoutVersion.NEW,
      ...(isMobileDevice && { isMobileDevice: true }),
    },
    withCredentials: true,
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export type PostSaferpayTokenParams = { token: string; saveForFutureUse?: boolean };
export const postSaferpayToken = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  { token, saveForFutureUse = false }: PostSaferpayTokenParams
): Promise<SaferpayAltStatus> => {
  const url = createUrl(servicesEndpoint, checkoutEndpoints.postSaferpayToken, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url,
    params: defaultRequestParams,
    withCredentials: true,
    data: {
      saferpayFieldsToken: token,
      saveForFutureUse: saveForFutureUse,
      checkoutVersion: CheckoutVersion.NEW,
    },
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const postOrderPaidByVoucher = async ({
  servicesEndpoint,
  isServer,
  defaultRequestParams,
}: QueryApiOptions): Promise<OrderInfo> => {
  const url = createUrl(servicesEndpoint, checkoutEndpoints.postOrderPaidByVoucher, isServer);

  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url,
    params: defaultRequestParams,
    withCredentials: true,
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const postPayment = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  data: PaymentData
): Promise<PaymentProviderInformation> => {
  const url = createUrl(servicesEndpoint, checkoutEndpoints.postPayment, isServer);

  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url,
    params: defaultRequestParams,
    data,
    withCredentials: true,
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const postPaymentProviderInput = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  provider: PaymentProvider
): Promise<{ redirectUrl: string; clientToken?: string }> => {
  const url = createUrl(servicesEndpoint, checkoutEndpoints.postPaymentProviderInput, isServer, {
    provider: provider.toLowerCase(),
  });

  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url,
    params: { ...defaultRequestParams, checkoutVersion: CheckoutVersion.NEW },
    withCredentials: true,
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const postSubmitOrder = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  { provider, authorizationToken }: PostSubmitOrderParams
): Promise<string> => {
  const url = createUrl(servicesEndpoint, checkoutEndpoints.postSubmitOrder, isServer, {
    provider: provider.toLowerCase(),
  });

  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url,
    params: { ...defaultRequestParams, authorizationToken, checkoutVersion: CheckoutVersion.NEW },
    withCredentials: true,
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const postSaferpayAlias = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  aliasId: string
): Promise<SaferpayAliasStatus> => {
  const url = createUrl(servicesEndpoint, checkoutEndpoints.postSaferpayAlias, isServer);

  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url,
    withCredentials: true,
    params: defaultRequestParams,
    data: { aliasId, checkoutVersion: CheckoutVersion.NEW },
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const postSubmitVoucher = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  { code, webcode }: PostVoucherSubmitParams
): Promise<AppliedVoucher> => {
  const url = createUrl(servicesEndpoint, checkoutEndpoints.postSubmitVoucher, isServer);

  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url,
    withCredentials: true,
    params: {
      ...defaultRequestParams,
      voucher: code,
      ...(webcode && { controlCode: webcode }),
    },
    data: { code, webcode },
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const postShoplandingPayment = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  shopNumber: string
): Promise<void> => {
  const url = createUrl(servicesEndpoint, checkoutEndpoints.postShoplandingPayment, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url,
    params: { ...defaultRequestParams, shopNumber, physicalShop: true },
    withCredentials: true,
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const getPaymentRequest = async (
  { servicesEndpoint, defaultRequestParams }: QueryApiOptions,
  paymentRequestId: string
): Promise<PaymentRequestInfo> => {
  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url: `${servicesEndpoint}${paymentRequestEndpoints.paymentRequest}`,
    params: {
      ...defaultRequestParams,
      paymentRequestId,
    },
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const getPaymentRequestConfirmation = async (
  { servicesEndpoint, defaultRequestParams }: QueryApiOptions,
  paymentRequestId: string
): Promise<PaymentRequestConfirmation> => {
  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url: `${servicesEndpoint}${paymentRequestEndpoints.paymentRequestConfirmation}`,
    params: {
      ...defaultRequestParams,
      paymentRequestId,
    },
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const initSaferpayAlternative = async (
  { servicesEndpoint, defaultRequestParams }: QueryApiOptions,
  paymentRequestId: string
): Promise<AlternativePaymentData> => {
  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url: `${servicesEndpoint}${paymentRequestEndpoints.initSaferpayAlternative}`,
    params: defaultRequestParams,
    data: {
      fictiveWebshopId: defaultRequestParams.shopId,
      market: defaultRequestParams.market,
      paymentRequestId,
    },
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const statusSaferpayAlternative = async (
  { servicesEndpoint }: QueryApiOptions,
  requestId: string
): Promise<SaferpayAltStatus> => {
  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url: `${servicesEndpoint}${paymentRequestEndpoints.statusSaferpayAlternative}`,
    params: requestId,
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const postRequestSaferpayToken = async (
  { servicesEndpoint, defaultRequestParams }: QueryApiOptions,
  { paymentRequestId, saferpayFieldsToken }: PostRequestSaferpayTokenParams
): Promise<{ redirectUrl: string }> => {
  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url: `${servicesEndpoint}${paymentRequestEndpoints.paymentSaferpayToken}`,
    params: defaultRequestParams,
    data: {
      fictiveWebshopId: defaultRequestParams.shopId,
      market: defaultRequestParams.market,
      saveForFutureUse: false,
      paymentRequestId,
      saferpayFieldsToken,
    },
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const postPaymentRequest = async (
  { servicesEndpoint, defaultRequestParams }: QueryApiOptions,
  { paymentRequestId, payment }: PostPaymentRequestParams
): Promise<PaymentRequestProviderInfo> => {
  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url: `${servicesEndpoint}${paymentRequestEndpoints.postPaymentRequest}`,
    params: {
      ...defaultRequestParams,
      paymentRequestId,
    },
    data: payment,
  };
  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const paymentProviderInputRequest = async (
  { servicesEndpoint, defaultRequestParams }: QueryApiOptions,
  { provider, data }: PaymentProviderInputRequestParams
): Promise<{ redirectUrl: string }> => {
  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url: `${servicesEndpoint}${paymentRequestEndpoints.providerPaymentRequest}/${provider.toLowerCase() as string}`,
    params: defaultRequestParams,
    data,
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};
